import React from 'react';
import { useNavigate } from 'react-router-dom';
import { specialOffers, loyaltyBenefits } from '../data/specialOffers';
import { Gift, Award, Star, Calendar, Tag } from 'lucide-react';

const OffersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-luxury-navy via-luxury-charcoal to-luxury-navy pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 px-6 py-2 rounded-full mb-4">
            <p className="text-white font-bold text-sm flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Special Offers & Rewards
            </p>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            🎉 Exclusive Deals for You!
          </h1>
          <p className="text-white/70 text-lg">
            Save more with our special offers and loyalty program
          </p>
        </div>

        {/* Active Offers Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {specialOffers.deals.filter(deal => deal.active).map((deal) => (
            <div
              key={deal.id}
              className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-2xl p-6 border-2 border-purple-400/40 shadow-xl hover:scale-105 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Tag className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{deal.title}</h3>
                    <span className="inline-block px-3 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-bold rounded-full mt-1">
                      {deal.badge}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">{deal.discount}</p>
                </div>
              </div>
              
              <p className="text-white/80 mb-4">{deal.description}</p>
              
              {deal.code && (
                <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
                  <p className="text-amber-300 text-sm font-semibold">
                    Code: <span className="text-white font-bold text-lg">{deal.code}</span>
                  </p>
                </div>
              )}
              
              {deal.validDays && (
                <div className="mt-3 flex items-center gap-2 text-white/60 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {deal.validDays.length === 7 ? 'All Week' : 
                     deal.validDays.includes(6) && deal.validDays.includes(0) ? 'Weekends Only' :
                     'Weekdays Only'}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Loyalty Program Section */}
        <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 backdrop-blur-sm rounded-3xl p-8 border-2 border-amber-500/30 mb-12">
          <div className="text-center mb-8">
            <Award className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-amber-300 mb-2">Loyalty Program Benefits</h2>
            <p className="text-white/70">Earn points with every purchase and unlock exclusive rewards!</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {loyaltyBenefits.tiers.map((tier, index) => (
              <div
                key={tier.name}
                className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 border-2 hover:scale-105 transition-all"
                style={{ borderColor: tier.color + '60' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Star className="w-8 h-8" style={{ color: tier.color }} />
                  <h3 className="text-2xl font-bold" style={{ color: tier.color }}>{tier.name}</h3>
                </div>
                
                <p className="text-white/60 text-sm mb-4">
                  {tier.minPoints > 0 ? `${tier.minPoints}+ points` : 'Starting Level'}
                </p>
                
                <ul className="space-y-2">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center p-6 bg-green-500/10 border border-green-400/30 rounded-xl">
            <p className="text-green-300 font-bold text-lg mb-2">
              💰 {loyaltyBenefits.redemption.value}
            </p>
            <p className="text-white/70 text-sm">
              Minimum {loyaltyBenefits.redemption.minimumPoints} points required for redemption
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/customer-login')}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
          >
            Join Loyalty Program
          </button>
          <button
            onClick={() => navigate('/order')}
            className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
          >
            Order Now & Earn Points
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffersPage;
